// src/components/Control/Operator/ControlPad.tsx
// Ручное управление: кнопки направлений (держать — едет, отпустил — стоп),
// виртуальный джойстик (отклонение задаёт направление и долю скорости),
// скорость и STOP. Протокол — общий с клавиатурой (robot/drive.ts).
import React, { useEffect, useRef, useState } from 'react';
import { useRobot, useDriveState } from '../robot/RobotContext';
import { Move, SPEED_MAX, SPEED_MIN, SPEED_STEP, TURN_PER_SPEED } from '../robot/drive';
import { StopButton } from './Header';
import { Segmented } from './ui';
import { loadPref, savePref } from '../../../types/features';

const DIRS: [Move, string, string][] = [
  ['forward', '↑', 'Вперёд'],
  ['left', '←', 'Влево'],
  ['right', '→', 'Вправо'],
  ['backward', '↓', 'Назад'],
];

export const DirectionPad: React.FC = () => {
  const { drive } = useRobot();
  const held = useDriveState(d => d.held);
  const [pressed, setPressed] = useState<Move | null>(null);
  const press = (m: Move) => (e: React.PointerEvent) => {
    e.preventDefault();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch { /* указатель уже отпущен */ }
    setPressed(m);
    const { v, w } = drive.vector([m]);
    drive.hold('buttons', v, w);
  };
  const up = () => {
    setPressed(null);
    drive.release('buttons');
  };
  // Клавиатура с кнопками на экране: Enter/пробел на кнопке — шаг 0.5 с
  const keyPress = (m: Move) => (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const { v, w } = drive.vector([m]);
    drive.hold('buttons', v, w);
    setTimeout(() => drive.release('buttons'), 500);
  };
  useEffect(() => () => drive.release('buttons'), [drive]);
  const btn = ([m, arrow, label]: [Move, string, string]) => (
    <button key={m} type="button" className={`op-dir ${m} ${pressed === m || held.includes(m) ? 'down' : ''}`}
            aria-label={`${label} (удерживайте)`} onPointerDown={press(m)} onPointerUp={up} onPointerCancel={up}
            onLostPointerCapture={up} onKeyDown={keyPress(m)} onContextMenu={e => e.preventDefault()}>
      <span aria-hidden="true">{arrow}</span>
    </button>
  );
  return (
    <div className="op-dpad" role="group" aria-label="Кнопки направления">
      {btn(DIRS[0])}
      {btn(DIRS[1])}
      <span className="op-dpad-center" aria-hidden="true">●</span>
      {btn(DIRS[2])}
      {btn(DIRS[3])}
    </div>
  );
};

export const Joystick: React.FC<{ size?: number }> = ({ size = 180 }) => {
  const { drive } = useRobot();
  const speed = useDriveState(d => d.speed);
  const [knob, setKnob] = useState<{ x: number; y: number } | null>(null);
  const activeRef = useRef(false);
  const baseRef = useRef<HTMLDivElement>(null);
  const r = size / 2;
  const knobR = size * 0.18;

  const move = (e: React.PointerEvent) => {
    const rect = baseRef.current!.getBoundingClientRect();
    let x = e.clientX - (rect.left + r);
    let y = e.clientY - (rect.top + r);
    const max = r - knobR;
    const d = Math.hypot(x, y);
    if (d > max) {
      x = (x / d) * max;
      y = (y / d) * max;
    }
    setKnob({ x, y });
    // Мёртвая зона 12%, дальше — доля выбранной скорости
    const nx = x / max;
    const ny = -y / max;
    const dz = (v: number) => (Math.abs(v) < 0.12 ? 0 : (v - Math.sign(v) * 0.12) / 0.88);
    const v = dz(ny) * speed;
    const w = -dz(nx) * speed * TURN_PER_SPEED;
    drive.hold('joystick', v, w);
  };
  const end = () => {
    activeRef.current = false;
    setKnob(null);
    drive.release('joystick');
  };
  useEffect(() => () => drive.release('joystick'), [drive]);

  return (
    <div ref={baseRef} className={`op-joystick ${knob ? 'active' : ''}`} style={{ width: size, height: size }}
         role="application" aria-label="Джойстик: потяните в сторону движения, отпустите — стоп"
         onPointerDown={e => {
           e.preventDefault();
           try {
             (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
           } catch { /* указатель уже отпущен */ }
           activeRef.current = true;
           move(e);
         }}
         onPointerMove={e => activeRef.current && move(e)} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end}>
      <span className="op-joy-mark n" aria-hidden="true">↑</span>
      <span className="op-joy-mark s" aria-hidden="true">↓</span>
      <span className="op-joy-mark w" aria-hidden="true">←</span>
      <span className="op-joy-mark e" aria-hidden="true">→</span>
      <div className="op-joy-knob" style={{
        width: knobR * 2, height: knobR * 2,
        transform: `translate(${(knob?.x ?? 0) - knobR}px, ${(knob?.y ?? 0) - knobR}px)`,
      }} />
    </div>
  );
};

export const SpeedControl: React.FC = () => {
  const { drive } = useRobot();
  const speed = useDriveState(d => d.speed);
  return (
    <label className="op-speed">
      <span>Скорость</span>
      <input type="range" min={SPEED_MIN} max={SPEED_MAX} step={SPEED_STEP} value={speed}
             onChange={e => drive.setSpeed(Number(e.target.value))} aria-valuetext={`${speed.toFixed(2)} метра в секунду`} />
      <output>{speed.toFixed(2)} м/с</output>
    </label>
  );
};

export const KeyboardHelp: React.FC = () => {
  const { drive } = useRobot();
  const enabled = useDriveState(d => d.keyboard);
  const held = useDriveState(d => d.held);
  const sent = useDriveState(d => d.lastSent);
  const k = (label: string, move?: Move) => <kbd className={move && held.includes(move) ? 'down' : ''}>{label}</kbd>;
  return (
    <div className="op-kbd">
      <label className="op-check">
        <input type="checkbox" checked={enabled} onChange={e => drive.setKeyboard(e.target.checked)} />
        Управление с клавиатуры
      </label>
      {enabled && (
        <div className="op-kbd-keys">
          <span>{k('↑', 'forward')} {k('W', 'forward')}</span><span>вперёд</span>
          <span>{k('↓', 'backward')} {k('S', 'backward')}</span><span>назад</span>
          <span>{k('←', 'left')} {k('A', 'left')} {k('→', 'right')} {k('D', 'right')}</span><span>влево / вправо (держите)</span>
          <span>{k('+')} {k('−')}</span><span>быстрее / медленнее</span>
          <span>{k('M')}</span><span>рассказать, что на карте</span>
          <span>{k('Пробел')} {k('Esc')}</span><span>STOP (и выход из автономии)</span>
        </div>
      )}
      {sent && <div className={`op-sent ${sent.ok ? '' : 'bad'}`}>{sent.text}</div>}
    </div>
  );
};

export type PadKind = 'buttons' | 'joystick';

// Полный набор управления: для раскрытой панели (desktop) и нижней шторки (mobile)
const ControlPad: React.FC<{ variant: 'desktop' | 'mobile' }> = ({ variant }) => {
  const [kind, setKind] = useState<PadKind>(() => loadPref('ui.pad', variant === 'mobile' ? 'joystick' : 'buttons'));
  const stoppedAt = useDriveState(d => d.stoppedAt);
  const [, force] = useState(0);
  useEffect(() => {
    if (!stoppedAt) return;
    const t = setTimeout(() => force(x => x + 1), 3000);
    return () => clearTimeout(t);
  }, [stoppedAt]);
  const stopped = Date.now() - stoppedAt < 3000;
  return (
    <div className={`op-pad ${variant}`}>
      <div className="op-pad-top">
        <Segmented label="Тип управления" value={kind} onChange={v => { setKind(v); savePref('ui.pad', v); }}
                   options={[['buttons', 'Кнопки'], ['joystick', 'Джойстик']]} />
      </div>
      <div className="op-pad-main">
        {kind === 'buttons' ? <DirectionPad /> : <Joystick size={variant === 'mobile' ? 200 : 168} />}
      </div>
      <SpeedControl />
      <div className="op-pad-stop">
        <StopButton />
        {stopped && <span className="op-stopped" role="status">■ Робот остановлен</span>}
      </div>
      {variant === 'desktop' && <KeyboardHelp />}
    </div>
  );
};

export default ControlPad;
